// @ts-nocheck
/* Three-way diff engine for MDD table metadata (ported from MetaCroc prototype). */

const ABSENT = Symbol('absent');

function isAbsent(v) { return v === ABSENT; }
function isLeaf(v) { return v === null || v === undefined || typeof v !== 'object'; }
function eq(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object') {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
  }
  return false;
}

function identityKey(obj) {
  if (obj == null || typeof obj !== 'object') return null;
  if (obj.uuid) return 'uuid:' + obj.uuid;
  if (obj.code) return 'code:' + obj.code;
  if (obj.id != null) return 'id:' + obj.id;
  return null;
}

function entityLabel(obj, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  return obj.code || obj.name || fallback;
}

// Statuses that count as "any change"
const CHANGED_STATUSES = new Set([
  'modified-src', 'modified-tgt', 'added-src', 'added-tgt',
  'removed-src', 'removed-tgt', 'auto', 'added-both', 'removed-both',
  'conflict', 'added-conflict',
]);
const CONFLICT_STATUSES = new Set(['conflict', 'added-conflict']);
const ADDED_STATUSES = new Set(['added-src', 'added-tgt', 'added-both', 'added-conflict']);
const REMOVED_STATUSES = new Set(['removed-src', 'removed-tgt', 'removed-both']);

function defaultResolution(status) {
  // Auto-pick where the answer is unambiguous.
  switch (status) {
    case 'unchanged': return null;
    case 'auto': return 'source';      // src == tgt, either works
    case 'added-both': return 'source';
    case 'removed-both': return 'source'; // both removed → keep removed
    case 'modified-src': return 'source';
    case 'modified-tgt': return 'target';
    case 'added-src': return 'source';
    case 'added-tgt': return 'target';
    case 'removed-src': return 'source'; // means: keep removed (drop)
    case 'removed-tgt': return 'target'; // keep removed
    case 'conflict':
    case 'added-conflict':
      return null; // user must choose
    default: return null;
  }
}

function buildDiffTree(base, source, target) {
  return diffNode('$', '(root)', base, source, target);
}

// A field that is absent on one side and an empty collection on another carries no
// information (the backend emits these constantly, e.g. odcsDqRules: []).
function isVacuous(v) {
  return v === undefined || (Array.isArray(v) && v.length === 0);
}

function diffNode(path, label, base, source, target) {
  if (isVacuous(base) && isVacuous(source) && isVacuous(target)) {
    const node = makeNode(path, label, base, source, target, 'unchanged');
    node.kind = 'leaf';
    return node;
  }

  const presentBase = base !== undefined;
  const presentSrc = source !== undefined;
  const presentTgt = target !== undefined;

  // Handle presence asymmetry (added/removed) BEFORE recursing
  if (!presentBase && (presentSrc || presentTgt)) {
    // newly introduced
    if (presentSrc && presentTgt) {
      const same = eq(source, target);
      const node = makeNode(path, label, base, source, target, same ? 'added-both' : 'added-conflict');
      // still recurse so the UI can drill into differences inside an added-conflict subtree
      if (!isLeaf(source) || !isLeaf(target)) {
        node.children = childrenFor(path, base, source, target);
        node.kind = Array.isArray(source) || Array.isArray(target) ? 'array' : 'object';
      } else {
        node.kind = 'leaf';
      }
      node.resolution = defaultResolution(node.status);
      return rollupCounts(node);
    }
    if (presentSrc) {
      const node = makeNode(path, label, base, source, target, 'added-src');
      if (!isLeaf(source)) {
        node.children = childrenFor(path, undefined, source, undefined);
        node.kind = Array.isArray(source) ? 'array' : 'object';
      } else node.kind = 'leaf';
      node.resolution = defaultResolution(node.status);
      return rollupCounts(node);
    }
    const node = makeNode(path, label, base, source, target, 'added-tgt');
    if (!isLeaf(target)) {
      node.children = childrenFor(path, undefined, undefined, target);
      node.kind = Array.isArray(target) ? 'array' : 'object';
    } else node.kind = 'leaf';
    node.resolution = defaultResolution(node.status);
    return rollupCounts(node);
  }

  if (presentBase && !presentSrc && !presentTgt) {
    const node = makeNode(path, label, base, source, target, 'removed-both');
    node.kind = isLeaf(base) ? 'leaf' : (Array.isArray(base) ? 'array' : 'object');
    node.resolution = defaultResolution(node.status);
    return rollupCounts(node);
  }
  if (presentBase && !presentSrc) {
    const node = makeNode(path, label, base, source, target, 'removed-src');
    node.kind = isLeaf(base) ? 'leaf' : (Array.isArray(base) ? 'array' : 'object');
    node.resolution = defaultResolution(node.status);
    return rollupCounts(node);
  }
  if (presentBase && !presentTgt) {
    const node = makeNode(path, label, base, source, target, 'removed-tgt');
    node.kind = isLeaf(base) ? 'leaf' : (Array.isArray(base) ? 'array' : 'object');
    node.resolution = defaultResolution(node.status);
    return rollupCounts(node);
  }

  // All three present (or both src/tgt and undef base only handled above)
  if (isLeaf(base) && isLeaf(source) && isLeaf(target)) {
    const baseEqSrc = eq(base, source);
    const baseEqTgt = eq(base, target);
    let status;
    if (baseEqSrc && baseEqTgt) status = 'unchanged';
    else if (!baseEqSrc && !baseEqTgt) status = eq(source, target) ? 'auto' : 'conflict';
    else if (!baseEqSrc) status = 'modified-src';
    else status = 'modified-tgt';
    const node = makeNode(path, label, base, source, target, status);
    node.kind = 'leaf';
    node.resolution = defaultResolution(status);
    return node;
  }

  // Container
  const isArr = Array.isArray(base) || Array.isArray(source) || Array.isArray(target);
  const node = makeNode(path, label, base, source, target, 'unchanged');
  node.kind = isArr ? 'array' : 'object';
  node.children = childrenFor(path, base, source, target);
  return rollupCounts(node);
}

function childrenFor(path, base, source, target) {
  const isArr = Array.isArray(base) || Array.isArray(source) || Array.isArray(target);
  if (isArr) {
    const b = Array.isArray(base) ? base : [];
    const s = Array.isArray(source) ? source : [];
    const t = Array.isArray(target) ? target : [];
    // Try identity-keyed merge
    const keyed = [...b, ...s, ...t].every((it) => identityKey(it) != null);
    if (keyed && (b.length + s.length + t.length) > 0) {
      const seen = new Set();
      const order = [];
      const collect = (arr) => {
        for (const it of arr) {
          const k = identityKey(it);
          if (!seen.has(k)) { seen.add(k); order.push(k); }
        }
      };
      // order preference: base order, then source-only adds, then target-only adds
      collect(b); collect(s); collect(t);
      return order.map((k) => {
        const bi = b.find((x) => identityKey(x) === k);
        const si = s.find((x) => identityKey(x) === k);
        const ti = t.find((x) => identityKey(x) === k);
        const sample = bi || si || ti;
        const label = entityLabel(sample, k);
        return diffNode(path + '/' + k, label, bi, si, ti);
      });
    }
    // primitive / unkeyed array → compare positionally
    const maxLen = Math.max(b.length, s.length, t.length);
    const kids = [];
    for (let i = 0; i < maxLen; i++) {
      kids.push(diffNode(path + '/[' + i + ']', '[' + i + ']', b[i], s[i], t[i]));
    }
    return kids;
  }
  // object
  const keys = new Set([
    ...(base ? Object.keys(base) : []),
    ...(source ? Object.keys(source) : []),
    ...(target ? Object.keys(target) : []),
  ]);
  const kids = [];
  for (const k of keys) {
    kids.push(diffNode(
      path + '/' + k, k,
      base ? base[k] : undefined,
      source ? source[k] : undefined,
      target ? target[k] : undefined,
    ));
  }
  // Preferred ordering: scalars first, then objects/arrays
  kids.sort((a, b) => {
    const aLeaf = a.kind === 'leaf' ? 0 : 1;
    const bLeaf = b.kind === 'leaf' ? 0 : 1;
    if (aLeaf !== bLeaf) return aLeaf - bLeaf;
    return a.label.localeCompare(b.label);
  });
  return kids;
}

function makeNode(path, label, base, source, target, status) {
  return { path, label, base, source, target, status, resolution: null, children: undefined };
}

function rollupCounts(node) {
  const counts = { conflict: 0, changed: 0, added: 0, removed: 0, total: 0 };
  function visit(n) {
    counts.total++;
    if (CONFLICT_STATUSES.has(n.status)) counts.conflict++;
    if (CHANGED_STATUSES.has(n.status) && n.status !== 'unchanged') counts.changed++;
    if (ADDED_STATUSES.has(n.status)) counts.added++;
    if (REMOVED_STATUSES.has(n.status)) counts.removed++;
    if (n.children) n.children.forEach(visit);
  }
  if (node.children) node.children.forEach(visit);
  // own status rollup if was 'unchanged' placeholder
  if (node.status === 'unchanged' && counts.changed > 0) {
    node.status = counts.conflict > 0 ? 'has-conflict' : 'has-changes';
  }
  node.counts = counts;
  return node;
}

/* ---- Resolution helpers --------------------------------- */

// Walk and set resolution everywhere
function setAllResolutions(node, choice) {
  if (CHANGED_STATUSES.has(node.status)) node.resolution = choice;
  if (node.children) node.children.forEach((c) => setAllResolutions(c, choice));
}

function setResolution(root, path, choice, customValue) {
  const node = findNode(root, path);
  if (!node) return false;
  node.resolution = choice;
  if (choice === 'custom') node.customValue = customValue;
  return true;
}

function findNode(root, path) {
  if (root.path === path) return root;
  if (!root.children) return null;
  for (const c of root.children) {
    const f = findNode(c, path);
    if (f) return f;
  }
  return null;
}

// Build the merged value tree from current resolutions
function buildMerged(node) {
  if (node.kind === 'leaf') {
    return resolveLeaf(node);
  }
  if (node.kind === 'array') {
    const out = [];
    if (node.children) {
      for (const c of node.children) {
        const v = buildMergedEntry(c);
        if (v !== ABSENT) out.push(v);
      }
    }
    return out;
  }
  // object
  if (CHANGED_STATUSES.has(node.status) && !node.children) {
    return resolveLeaf(node);
  }
  const out = {};
  if (node.children) {
    for (const c of node.children) {
      const v = buildMergedEntry(c);
      if (v !== ABSENT) out[c.label] = v;
    }
  }
  return out;
}

function buildMergedEntry(node) {
  // for added/removed nodes the "value" is the whole sub-object
  switch (node.status) {
    case 'unchanged':
      return node.base;
    case 'auto':
    case 'added-both':
      return node.source;
    case 'removed-both':
      return ABSENT;
    case 'modified-src':
    case 'added-src':
      if (node.resolution === 'target') return ABSENT;
      if (node.resolution === 'custom') return node.customValue;
      return node.source;
    case 'modified-tgt':
    case 'added-tgt':
      if (node.resolution === 'source') return ABSENT;
      if (node.resolution === 'custom') return node.customValue;
      return node.target;
    case 'removed-src':
      // base+target have it, source dropped it
      if (node.resolution === 'source') return ABSENT;
      if (node.resolution === 'custom') return node.customValue;
      return node.target;
    case 'removed-tgt':
      if (node.resolution === 'target') return ABSENT;
      if (node.resolution === 'custom') return node.customValue;
      return node.source;
    case 'conflict':
    case 'added-conflict':
      if (node.resolution === 'target') return node.target;
      if (node.resolution === 'custom') return node.customValue;
      if (node.resolution === 'source') return node.source;
      // Unresolved → fall back to source side, but mark
      return node.source ?? node.target;
    case 'has-conflict':
    case 'has-changes':
      // container with mixed children — recurse
      return buildMerged(node);
    default:
      return node.base ?? node.source ?? node.target;
  }
}

function resolveLeaf(node) {
  switch (node.status) {
    case 'unchanged':
    case 'auto':
      return node.source !== undefined ? node.source : node.base;
    case 'modified-src':
    case 'added-src':
      if (node.resolution === 'target') return node.target;
      if (node.resolution === 'custom') return node.customValue;
      return node.source;
    case 'modified-tgt':
    case 'added-tgt':
      if (node.resolution === 'source') return node.source;
      if (node.resolution === 'custom') return node.customValue;
      return node.target;
    case 'removed-src':
      if (node.resolution === 'source') return undefined;
      if (node.resolution === 'custom') return node.customValue;
      return node.target;
    case 'removed-tgt':
      if (node.resolution === 'target') return undefined;
      if (node.resolution === 'custom') return node.customValue;
      return node.source;
    case 'conflict':
    case 'added-conflict':
      if (node.resolution === 'target') return node.target;
      if (node.resolution === 'custom') return node.customValue;
      return node.source;
    default:
      return node.base;
  }
}

// flatten a row list for export / table views
function flatten(node, out = []) {
  if (node.path !== '$') out.push(node);
  if (node.children) node.children.forEach((c) => flatten(c, out));
  return out;
}

/* ---- Public --------------------------------------------- */
export const DiffEngine = {
  ABSENT,
  buildDiffTree,
  setResolution,
  setAllResolutions,
  findNode,
  buildMerged,
  flatten,
  identityKey,
  CHANGED_STATUSES,
  CONFLICT_STATUSES,
  ADDED_STATUSES,
  REMOVED_STATUSES,
};
